/**
 * Markdown to the HTML a word processor will take.
 *
 * ## Why this exists at all
 *
 * The transcript on screen is *rendered* markdown: a table is box-drawing
 * characters, a code block is a bordered panel, a heading is a coloured chip, a
 * long line is hard-wrapped to the terminal's width. Selecting that with the
 * mouse and pasting it into Word or Confluence pastes exactly what it looks
 * like — dotted rules, broken table edges, wrapping frozen at eighty columns —
 * because the terminal's clipboard carries glyphs and nothing else.
 *
 * What the user wanted to copy is the *structure*, and the structure is still
 * in the markdown the model wrote. So the copy path goes back to that source
 * and offers it twice: the markdown itself as plain text, and this, as HTML,
 * for anything that knows how to take a rich paste. Word turns `<h2>` into a
 * heading and `<table>` into a table; Confluence does the same; a plain text
 * field gets the markdown and is none the wiser.
 *
 * ## What it is not
 *
 * Not a markdown implementation. It is the subset an agent transcript actually
 * contains — headings, emphasis, code, links, lists, quotes, rules and GFM
 * tables — rendered as the plainest HTML that survives a paste. Anything it
 * does not recognise comes through as text, which is the failure a reader can
 * still work with. There is no HTML passthrough: source markdown is untrusted
 * input as far as this is concerned, every `<` is escaped, and a model that
 * wrote `<script>` gets `&lt;script&gt;` pasted into the document.
 *
 * ## Why the styling is inline
 *
 * A clipboard fragment carries no stylesheet. Word keeps inline `style`
 * attributes and drops everything else, so the handful of rules that make code
 * read as code and a table read as a table ride on the elements themselves.
 */

/** Escape the five characters that are markup, so text stays text. */
function escapeHtml(text: string): string {
	return text
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&#39;");
}

/** The styling that has to survive a paste, so it travels on the element. */
const STYLE = {
	code: 'style="font-family:Consolas,Menlo,monospace;background:#f4f4f4;padding:1px 4px;border-radius:3px"',
	pre: 'style="font-family:Consolas,Menlo,monospace;background:#f4f4f4;padding:10px;border-radius:4px;white-space:pre-wrap"',
	table: 'style="border-collapse:collapse"',
	cell: 'style="border:1px solid #bbb;padding:4px 8px;text-align:left"',
	quote: 'style="border-left:3px solid #bbb;margin:0 0 0 8px;padding-left:10px;color:#555"',
} as const;

/**
 * Inline markup, innermost first.
 *
 * Code spans are taken out before anything else and put back at the end:
 * whatever is inside one is literal, and an asterisk in a shell snippet is not
 * an emphasis marker. Everything between is ordinary text and is escaped before
 * a single tag is introduced, so no pattern below can ever match markup this
 * function itself produced.
 */
function renderInline(markdown: string): string {
	const codeSpans: string[] = [];
	// A placeholder no markdown can contain and no pattern below can match.
	const stash = (html: string): string => ` ${codeSpans.push(html) - 1} `;

	let text = markdown.replace(/(`+)([^`]|[^`][\s\S]*?[^`])\1(?!`)/g, (_all, _ticks, code: string) =>
		stash(`<code ${STYLE.code}>${escapeHtml(code.trim())}</code>`),
	);

	text = escapeHtml(text);
	// Images before links: the syntax is a link with a bang, and the link
	// pattern would otherwise eat the `[...]` and leave the `!` stranded.
	text = text.replace(
		/!\[([^\]]*)\]\(([^)\s]+)(?:\s+&quot;[^&]*&quot;)?\)/g,
		(_all, alt: string, src: string) => `<img src="${src}" alt="${alt}">`,
	);
	text = text.replace(
		/\[([^\]]+)\]\(([^)\s]+)(?:\s+&quot;[^&]*&quot;)?\)/g,
		(_all, label: string, href: string) => `<a href="${href}">${label}</a>`,
	);
	// A bare URL on its own is a link too — agents write them constantly, and a
	// URL that is not clickable in the pasted document is a URL retyped by hand.
	text = text.replace(/(^|[\s(])(https?:\/\/[^\s<>()]+)/g, (_all, lead: string, url: string) => {
		const trailing = url.match(/[.,;:!?]+$/)?.[0] ?? "";
		const bare = url.slice(0, url.length - trailing.length);
		return `${lead}<a href="${bare}">${bare}</a>${trailing}`;
	});
	text = text.replace(/\*\*\*([^*]+)\*\*\*/g, "<strong><em>$1</em></strong>");
	text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
	text = text.replace(/(^|[^*\w])\*([^*\n]+)\*(?![*\w])/g, "$1<em>$2</em>");
	text = text.replace(/(^|[^_\w])_([^_\n]+)_(?![_\w])/g, "$1<em>$2</em>");
	text = text.replace(/~~([^~]+)~~/g, "<del>$1</del>");

	return text.replace(/ (\d+) /g, (_all, index: string) => codeSpans[Number(index)]);
}

/** One row of a GFM table, split on the pipes that are not escaped. */
function splitRow(line: string): string[] {
	return line
		.replace(/^\s*\|/, "")
		.replace(/\|\s*$/, "")
		.split(/(?<!\\)\|/)
		.map((cell) => cell.replace(/\\\|/g, "|").trim());
}

/** Whether `line` is the `|---|:--:|` rule that makes the row above a header. */
function isTableRule(line: string | undefined): boolean {
	return line !== undefined && /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && line.includes("-");
}

/** A list item's marker, if the line opens one. */
function listMarker(line: string): { indent: number; ordered: boolean; content: string } | undefined {
	const match = line.match(/^(\s*)([-*+]|\d+[.)])\s+(.*)$/);
	if (!match) return undefined;
	return { indent: match[1].length, ordered: /\d/.test(match[2]), content: match[3] };
}

/**
 * Render a markdown document as an HTML fragment (no `<html>`, no `<body>`).
 *
 * A fragment rather than a document because that is what a clipboard carries:
 * the platform wraps it in whatever envelope it needs (CF_HTML on Windows,
 * `«class HTML»` on macOS), and a full document inside that envelope is a
 * second `<body>` for the receiving application to make sense of.
 */
export function markdownToHtml(markdown: string): string {
	const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
	const html: string[] = [];
	/** Open list elements, outermost first, so nesting closes in order. */
	const lists: { indent: number; tag: "ul" | "ol" }[] = [];
	let paragraph: string[] = [];

	const closeLists = (toIndent = -1): void => {
		while (lists.length > 0 && lists[lists.length - 1].indent > toIndent) {
			html.push(`</${lists.pop()?.tag}>`);
		}
	};
	const flushParagraph = (): void => {
		if (paragraph.length === 0) return;
		// A single newline inside a paragraph is a line the author broke on
		// purpose often enough — an address, a list of names — that keeping it
		// costs less than losing it.
		html.push(`<p>${paragraph.map(renderInline).join("<br>")}</p>`);
		paragraph = [];
	};
	const flush = (): void => {
		flushParagraph();
		closeLists();
	};

	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];

		// Fenced code: taken whole, contents never interpreted.
		const fence = line.match(/^\s*(```+|~~~+)(.*)$/);
		if (fence) {
			flush();
			const closing = fence[1][0].repeat(3);
			const body: string[] = [];
			i += 1;
			while (i < lines.length && !lines[i].trimStart().startsWith(closing)) {
				body.push(lines[i]);
				i += 1;
			}
			html.push(`<pre ${STYLE.pre}><code>${escapeHtml(body.join("\n"))}</code></pre>`);
			continue;
		}

		if (line.trim() === "") {
			flushParagraph();
			continue;
		}

		const heading = line.match(/^(#{1,6})\s+(.*)$/);
		if (heading) {
			flush();
			const level = heading[1].length;
			html.push(`<h${level}>${renderInline(heading[2].replace(/\s+#+\s*$/, ""))}</h${level}>`);
			continue;
		}

		if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
			flush();
			html.push("<hr>");
			continue;
		}

		// A table is only a table with its rule: without one those pipes are
		// prose, and prose promoted to a table is unreadable in both forms.
		if (line.includes("|") && isTableRule(lines[i + 1])) {
			flush();
			const header = splitRow(line);
			const rows: string[][] = [];
			i += 2;
			while (i < lines.length && lines[i].includes("|") && lines[i].trim() !== "") {
				rows.push(splitRow(lines[i]));
				i += 1;
			}
			i -= 1;
			const cells = (row: string[], tag: "th" | "td"): string =>
				row.map((cell) => `<${tag} ${STYLE.cell}>${renderInline(cell)}</${tag}>`).join("");
			html.push(
				`<table ${STYLE.table}><thead><tr>${cells(header, "th")}</tr></thead><tbody>` +
					`${rows.map((row) => `<tr>${cells(row, "td")}</tr>`).join("")}</tbody></table>`,
			);
			continue;
		}

		const quote = line.match(/^\s*>\s?(.*)$/);
		if (quote) {
			flush();
			const body = [quote[1]];
			while (i + 1 < lines.length && /^\s*>/.test(lines[i + 1])) {
				body.push(lines[i + 1].replace(/^\s*>\s?/, ""));
				i += 1;
			}
			html.push(`<blockquote ${STYLE.quote}>${markdownToHtml(body.join("\n"))}</blockquote>`);
			continue;
		}

		const item = listMarker(line);
		if (item) {
			flushParagraph();
			const tag = item.ordered ? "ol" : "ul";
			const open = lists[lists.length - 1];
			if (!open || item.indent > open.indent) {
				lists.push({ indent: item.indent, tag });
				html.push(`<${tag}>`);
			} else {
				closeLists(item.indent);
				const current = lists[lists.length - 1];
				if (!current) {
					lists.push({ indent: item.indent, tag });
					html.push(`<${tag}>`);
				} else if (current.tag !== tag) {
					// The marker changed at the same depth: one list ended and
					// another began, which is what the author meant by changing it.
					html.push(`</${current.tag}>`);
					lists[lists.length - 1] = { indent: item.indent, tag };
					html.push(`<${tag}>`);
				}
			}
			html.push(`<li>${renderInline(item.content)}</li>`);
			continue;
		}

		closeLists();
		paragraph.push(line.trim());
	}

	flush();
	return html.join("\n");
}
